import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Linkedin,
  Link2,
  Mail,
  Check,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ArticleSchema from "@/components/ArticleSchema";
import BlogCard from "@/components/BlogCard";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  formatDateDE,
  getPostBySlug,
  getReadingMinutes,
  getRelatedPosts,
} from "@/lib/blog";

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const BlogArtikel = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const [copied, setCopied] = useState(false);

  if (!post) return <Navigate to="/blog" replace />;

  const url = `https://leadership-focus.de/blog/${post.slug}`;
  const minutes = getReadingMinutes(post.body);
  const related = getRelatedPosts(post.slug, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  const linkedinShare = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const mailto = `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(
    `${post.excerpt}\n\n${url}`,
  )}`;

  return (
    <>
      <SEOHead
        title={post.seoTitle || `${post.title} | Leadership Focus`}
        description={post.seoDescription || post.excerpt}
        canonical={url}
      />
      <ArticleSchema post={post} url={url} />
      <Header />
      <main className="min-h-[60vh]">
        <article className="container py-12 md:py-20 max-w-3xl">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zum Blog
            </Link>

            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-3">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground mb-8 pb-8 border-b">
              <span className="inline-flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.date}>{formatDateDE(post.date)}</time>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {minutes} Min Lesezeit
              </span>
            </div>

            {post.coverImage && (
              <div className="overflow-hidden rounded-lg bg-muted mb-10">
                <img
                  src={post.coverImage}
                  alt={post.coverAlt || post.title}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}

            {post.tldr && (
              <div className="bg-surface border-l-4 border-primary rounded-r-md p-5 mb-10">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">
                  Kurz gesagt
                </p>
                <p className="text-base text-foreground leading-relaxed">{post.tldr}</p>
              </div>
            )}

            {post.spotifyEmbedUrl && (
              <div className="mb-10">
                <iframe
                  src={post.spotifyEmbedUrl}
                  width="100%"
                  height="232"
                  frameBorder={0}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="Spotify Podcast Player"
                  className="rounded-xl"
                />
              </div>
            )}

            {post.youtubeEmbedUrl && (
              <div className="mb-10 aspect-video overflow-hidden rounded-xl bg-muted">
                <iframe
                  src={post.youtubeEmbedUrl}
                  width="100%"
                  height="100%"
                  frameBorder={0}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  title="YouTube Video Player"
                  className="w-full h-full"
                />
              </div>
            )}

            <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-blockquote:border-primary prose-blockquote:text-foreground/80 prose-strong:text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
            </div>

            {post.linkedinUrl && (
              <div className="mt-10 p-5 bg-surface rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">
                    Diskussion auf LinkedIn
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Diesen Artikel auf LinkedIn ansehen, kommentieren und teilen.
                  </p>
                </div>
                <Button asChild variant="default" className="gap-2 shrink-0">
                  <a href={post.linkedinUrl} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="w-4 h-4" />
                    Auf LinkedIn ansehen
                  </a>
                </Button>
              </div>
            )}

            {post.faq && post.faq.length > 0 && (
              <section className="mt-14">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-6">
                  Häufige Fragen
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {post.faq.map((item, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left text-base font-medium">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-base text-muted-foreground leading-relaxed">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            )}

            {post.tags && post.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                  Themen
                </p>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/blog?tag=${encodeURIComponent(t)}`}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-surface text-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                    >
                      #{t}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 pt-8 border-t">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Artikel teilen
              </p>
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline" className="gap-1.5">
                  <a href={linkedinShare} target="_blank" rel="noopener noreferrer">
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline" className="gap-1.5">
                  <a href={mailto}>
                    <Mail className="w-4 h-4" />
                    Per E-Mail
                  </a>
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={handleCopy}>
                  {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  {copied ? "Kopiert" : "Link kopieren"}
                </Button>
              </div>
            </div>
          </motion.div>
        </article>

        {related.length > 0 && (
          <section className="bg-surface py-16 md:py-20">
            <div className="container">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-8">
                Weitere Artikel
              </h2>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {related.map((p) => (
                  <BlogCard key={p.slug} post={p} compact />
                ))}
              </motion.div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};

export default BlogArtikel;
